/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Currency, { CurrencyType } from '../../Localization/Currency';
import LocalizationComponent from '../../Localization/LocalizationComponent';
import BetManager from '../../Playbar/BetManager';
import Communicator from '../../Server/Communicator';
import { ErrorPrompt } from '../../Server/Error/ErrorPrompt';
import IErrorParams from '../../Server/Error/IErrorParams';
import { IPlayResult, IPlayState } from '../../Server/Object/PlayResult';
import PlayResultComponent from '../../Server/PlayResultComponent';
import StateComponent from '../../StateManager/StateComponent';
import StateDependencyComponent from '../../StateManager/StateDependencyComponent';
import { StateID } from '../../StateManager/StateID';
import InjectableComponent from '../../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../../Utility/Component/ComponentReference/ReferenceGroup';
import { IPopupOptions } from '../../Utility/Component/Popup/IPopupModal';
import PopupModal from '../../Utility/Component/Popup/PopupModal';
import CLog, { LogTypes } from '../../Utility/ComponentLogger';
import { DisplayKey } from '../DisplayKey';
import GameSetup from '../GameSetup';
import RecoveryManager from '../RecoveryManager';
import { ClientEvent } from './ClientEvent';
import { IClientEventResultData, IClientEventWagerData } from './IClientEventData';
import IPlatformAdapter from './IPlatformAdapter';

export default class DefaultAdapter implements IPlatformAdapter
{
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    private _communicatorRef: InjectableComponent<Communicator> = new InjectableComponent();
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    private _popupModalRef: InjectableComponent<PopupModal> = new InjectableComponent();
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();
    private _lastWagerPlayId: number = -1;
    private _lastResultPlayId: number = -1;

    constructor()
    {
        this._betManagerRef.SetContext(BetManager);
        this._communicatorRef.SetContext(Communicator);
        this._gameSetupRef.SetContext(GameSetup);
        this._localRef.SetContext(LocalizationComponent);
        this._playResultRef.SetContext(PlayResultComponent);
        this._popupModalRef.SetContext(PopupModal);
        this._recoveryManagerRef.SetContext(RecoveryManager);
    }

    public GameLoaded(): void
    {
        this._communicatorRef.WithValue(communicator => communicator.ReportClientEvent(ClientEvent.GAME_LOADED));
    }

    public WagerRequested(state: StateComponent): void
    {
        if (state.ReferenceGroup === ReferenceGroup.MainGame && state.StateID === StateID.Request)
        {
            this._communicatorRef.Value
                    .ReportClientEvent(ClientEvent.WAGER_REQUESTED,
                    {
                        bet: this._betManagerRef.WithValue(mgr => mgr.LastWager.BetCredits),
                    } as IClientEventWagerData);
        }
    }

    public WagerMade(state: StateComponent): void
    {
        if (state.ReferenceGroup === ReferenceGroup.MainGame && state.StateID === StateID.Spinning)
        {
            const playID: number = this._playResultRef.Value.ResultData.PlayResult.PlaySequenceNumber;
            if (this._lastWagerPlayId === playID)
            {
                CLog.warn("Repeat client event, ignoring.  Play ID: " + playID, LogTypes.None);
                return;
            }
            this._lastWagerPlayId = playID;

            this._communicatorRef.Value
                .ReportClientEvent(ClientEvent.WAGER,
                {
                    bet: this._betManagerRef.WithValue(mgr => mgr.LastWager.BetCredits),
                    playID,
                } as IClientEventWagerData);
        }
    }

    public ReportError(errorCode: string, errorParams: IErrorParams, popupOptions?: IPopupOptions): void
    {
        let message: string = '';

        if (errorParams.textCode != null)
        {
            message = this._localRef.Value.GetStringResource(`Errors/${errorParams.textCode}`);
            if (!message)
            {
                message = this._localRef.Value.GetStringResource(`Errors/1`);
            }
        }

        if (!popupOptions)
        {
            popupOptions = { MessageId: errorCode };

            for (let promptIndex in errorParams.prompts)
            {
                const prompt: string = errorParams.prompts[promptIndex];

                // Prompted to exit to lobby, but Lobby is not available per configuration
                if (prompt === ErrorPrompt.ExitToLobby
                        && this._gameSetupRef.Value.ShouldDisplay(DisplayKey.Lobby) === false)
                {
                    message += ` ${this._localRef.Value.GetStringResource("Errors/Please_return_to_lobby")}`;

                    // If reload is not already one of the options, make it one.
                    if (errorParams.prompts.indexOf(ErrorPrompt.Reload) > -1) { continue; }

                    errorParams.prompts[promptIndex] = ErrorPrompt.Reload;
                }

                errorParams.handlePrompt(errorParams.prompts[promptIndex], popupOptions, parseInt(promptIndex, 10));
            }
        }
        popupOptions.Title = this._localRef.Value.GetStringResource(`Errors/Title`);

        if (errorParams && errorParams.traceId)
        {
            message
                += `\r\n${this._localRef.Value.GetStringResource("Errors/Server_Error_ID_")}${errorParams.traceId}`;
        }

        this._popupModalRef.WithValue(popupModal => popupModal.Show(message, popupOptions));
    }

    public ResultDisplayed(component: StateDependencyComponent, state: StateComponent): boolean
    {
        let playState: IPlayState;
        const playResult: IPlayResult = this._playResultRef.WithValue(r =>
        {
            playState = r.PlayState;
            return r.ResultData.PlayResult;
        });

        if (state.ReferenceGroup !== ReferenceGroup.MainGame
            || state.StateID !== StateID.PostPlay
            || playResult == null) { return false; }

        const betManager: BetManager = this._betManagerRef.Value;
        const bet: number = betManager.LastWager.BetCredits;
        const balance: number = (new Currency(betManager.LastWager.DenomMc, playState.balanceMc))
            .GetValue(CurrencyType.CREDITS);

        const win: number = playResult.TotalAward * betManager.LastWager.BetScaler;

        const playID: number = playResult.PlaySequenceNumber;
        if (this._lastResultPlayId === playID)
        {
            CLog.warn(`Repeat client event, ignoring.  Play ID: ${playID}`, LogTypes.None);
            return false;
        }
        this._lastResultPlayId = playID;

        const eventData: IClientEventResultData =
        {
            bet,
            win,
            finishRound: true, // TODO: this will be variable once we add progressives
            playID,
            balance
        };

        this._communicatorRef.Value.ReportClientEvent(ClientEvent.RESULT, eventData);
        return false;
    }

    public WalletNotification(notification: any): void
    {
        // stub: not implemented.
    }
}
